import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Table, Button, Space, Select, Checkbox, Tag, Modal, Form, Input, DatePicker, Segmented, message,
} from 'antd';
import { PlusOutlined, FolderOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { selectFilter } from '../utils/selectFilter';
import LinksFormList from '../components/LinksFormList';
import {
  PROJECT_STATUSES, PROJECT_STATUS_COLOR, useProjectOptions, MembersFormList, linksFromForm, typeOptions, projectPath,
} from '../components/ProjectParts';

// Oversight > Projects. On screen the levels are Folder (code: project) and
// Project inside it (code: folder) -- see ProjectParts. Folders view: the
// folders this user can see (as owner, member, project member or assignee),
// and New Folder for those who may create. Projects view: each project they
// can see with its folder -- for project-only members, and for finding a
// project by name. The search box filters either view by name; Campaign and
// Type narrow both.

// MyAccess 'Folder' (the code's word) means access to some projects only.
const ACCESS_LABEL = { Manager: 'Manager', Member: 'Member', Folder: 'Project', Task: 'Task only' };

export default function ProjectsPage() {
  const navigate = useNavigate();
  const options = useProjectOptions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [campaignId, setCampaignId] = useState(null);
  const [typeId, setTypeId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [view, setView] = useState('projects');
  const [folders, setFolders] = useState([]);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(() => {
    setLoading(true);
    const params = {
      ...(campaignId ? { campaign_id: campaignId } : {}), ...(typeId ? { type_id: typeId } : {}),
      ...(showArchived ? { archived: 1 } : {}),
    };
    api.get(view === 'folders' ? '/api/project-folders' : '/api/projects', { params })
      .then((r) => (view === 'folders' ? setFolders : setRows)(r.data || []))
      .catch(() => message.error(view === 'folders' ? 'Failed to load projects.' : 'Failed to load folders.'))
      .finally(() => setLoading(false));
  }, [campaignId, typeId, showArchived, view]);
  const needle = q.trim().toLowerCase();
  const matches = (...names) => !needle || names.some((n) => (n || '').toLowerCase().includes(needle));
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    let v;
    try { v = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const r = await api.post('/api/projects', {
        name: v.name, description: v.description || '', status: v.status || 'Active',
        start_date: v.dates?.[0]?.format('YYYY-MM-DD') || null, end_date: v.dates?.[1]?.format('YYYY-MM-DD') || null,
        campaign_ids: v.campaign_ids || [], members: v.members || [], links: linksFromForm(v.links),
        type_id: v.type_id ?? null,
      });
      setCreating(false);
      navigate(`/folders/${r.data.id}`);
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not create the folder.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Projects</Typography.Title>
        {options.canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreating(true); }}>New Folder</Button>
        )}
      </div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented value={view} onChange={setView}
          options={[{ value: 'projects', label: 'Folders' }, { value: 'folders', label: 'Projects' }]} />
        <Input.Search allowClear placeholder={view === 'folders' ? 'Search projects or folders' : 'Search folders'}
          style={{ width: 240 }} value={q} onChange={(e) => setQ(e.target.value)} />
        <span>Campaign:</span>
        <Select allowClear showSearch filterOption={selectFilter} placeholder="All campaigns" style={{ width: 220 }}
          value={campaignId} onChange={setCampaignId}
          options={options.campaigns.map((c) => ({ value: c.ID, label: c.Name }))} />
        <span>Type:</span>
        <Select allowClear showSearch filterOption={selectFilter} placeholder="All types" style={{ width: 200 }}
          value={typeId} onChange={setTypeId} options={options.types.map((t) => ({ value: t.ID, label: t.Name }))} />
        <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}>Show archived</Checkbox>
      </Space>
      {view === 'folders' ? (
        <Table
          rowKey="ID" size="small" loading={loading} dataSource={folders.filter((f) => matches(f.Name, f.ProjectName))}
          pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'], showTotal: (t) => `${t} ${t === 1 ? 'project' : 'projects'}` }}
          locale={{ emptyText: 'No projects you can see yet.' }}
          onRow={(f) => ({ onClick: () => navigate(projectPath(f.ProjectID, f.ID)), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'Project', dataIndex: 'Name', key: 'Name', sorter: (a, b) => a.Name.localeCompare(b.Name),
              render: (v, f) => (
                <Space size={6}>
                  <FolderOutlined />
                  <Typography.Text strong style={{ color: '#1677ff' }}>{v}</Typography.Text>
                  {f.IsArchived && <Tag>Archived</Tag>}
                </Space>
              ) },
            { title: 'Folder', dataIndex: 'ProjectName', key: 'ProjectName', width: 240,
              sorter: (a, b) => a.ProjectName.localeCompare(b.ProjectName),
              render: (v, f) => <>{v}{f.ProjectStatus !== 'Active' && <Tag style={{ marginLeft: 6 }}>{f.ProjectStatus}</Tag>}</> },
            { title: 'Type', dataIndex: 'TypeName', key: 'TypeName', width: 160,
              render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
            { title: 'Dates', key: 'dates', width: 190,
              sorter: (a, b) => (a.StartDate || '9999').localeCompare(b.StartDate || '9999'),
              render: (_, r) => (r.StartDate || r.EndDate ? `${r.StartDate || '…'} – ${r.EndDate || '…'}` : <Typography.Text type="secondary">—</Typography.Text>) },
            // The project's own campaigns, else its folder's (marked).
            { title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns', width: 220,
              render: (v, f) => (v?.length
                ? <>{v.map((c) => <Tag key={c.ID}>{c.Name}</Tag>)}
                    {!f.OwnCampaigns && <Typography.Text type="secondary" style={{ fontSize: 12 }}>(folder&apos;s)</Typography.Text>}</>
                : <Typography.Text type="secondary">—</Typography.Text>) },
            { title: 'Owner', dataIndex: 'OwnerName', key: 'OwnerName', width: 170 },
            { title: 'Open Tasks', dataIndex: 'OpenTasks', key: 'OpenTasks', width: 100, align: 'right',
              sorter: (a, b) => a.OpenTasks - b.OpenTasks },
            { title: 'My Access', dataIndex: 'MyAccess', key: 'MyAccess', width: 110, render: (v) => ACCESS_LABEL[v] || v },
          ]}
        />
      ) : (
      <Table
        rowKey="ID" size="small" loading={loading} dataSource={rows.filter((r) => matches(r.Name))}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['50', '100', '200', '500'], showTotal: (t) => `${t} ${t === 1 ? 'folder' : 'folders'}` }}
        locale={{ emptyText: 'No folders you can see yet.' }}
        onRow={(r) => ({ onClick: () => navigate(`/folders/${r.ID}`), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Folder', dataIndex: 'Name', key: 'Name', sorter: (a, b) => a.Name.localeCompare(b.Name),
            render: (v) => <Typography.Text strong style={{ color: '#1677ff' }}>{v}</Typography.Text> },
          { title: 'Type', dataIndex: 'TypeName', key: 'TypeName', width: 160,
            render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
          { title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns', width: 220,
            render: (v) => (v?.length ? v.map((c) => <Tag key={c.ID}>{c.Name}</Tag>) : <Typography.Text type="secondary">—</Typography.Text>) },
          { title: 'Owner', dataIndex: 'OwnerName', key: 'OwnerName', width: 170 },
          { title: 'Status', dataIndex: 'Status', key: 'Status', width: 110,
            filters: PROJECT_STATUSES.map((s) => ({ text: s, value: s })), onFilter: (v, r) => r.Status === v,
            render: (v) => <Tag color={PROJECT_STATUS_COLOR[v]}>{v}</Tag> },
          { title: 'Dates', key: 'dates', width: 190,
            render: (_, r) => (r.StartDate || r.EndDate ? `${r.StartDate || '…'} – ${r.EndDate || '…'}` : <Typography.Text type="secondary">—</Typography.Text>) },
          { title: 'Open Tasks', dataIndex: 'OpenTasks', key: 'OpenTasks', width: 100, align: 'right',
            sorter: (a, b) => a.OpenTasks - b.OpenTasks },
          { title: 'My Access', dataIndex: 'MyAccess', key: 'MyAccess', width: 110, render: (v) => ACCESS_LABEL[v] || v },
        ]}
      />
      )}

      <Modal maskClosable={false} title="New Folder" open={creating} onOk={create} onCancel={() => setCreating(false)}
        confirmLoading={saving} okText="Create" width={720} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }} initialValues={{ status: 'Active' }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input maxLength={200} placeholder="e.g. Campaign NEE QA" />
          </Form.Item>
          <Form.Item name="description" label="Description"><Input.TextArea autoSize={{ minRows: 2, maxRows: 8 }} /></Form.Item>
          <Space wrap size={12}>
            <Form.Item name="status" label="Status">
              <Select style={{ width: 140 }} options={PROJECT_STATUSES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
            <Form.Item name="dates" label="Dates"><DatePicker.RangePicker allowEmpty={[true, true]} /></Form.Item>
            <Form.Item name="type_id" label="Type">
              <Select allowClear showSearch filterOption={selectFilter} style={{ width: 220 }} placeholder="No type"
                options={typeOptions(options)} />
            </Form.Item>
          </Space>
          <Form.Item name="campaign_ids" label="Campaigns">
            <Select mode="multiple" allowClear showSearch filterOption={selectFilter}
              options={options.campaigns.map((c) => ({ value: c.ID, label: c.Name }))} />
          </Form.Item>
          <Form.Item label="Members (you are the owner)"><MembersFormList name="members" options={options} /></Form.Item>
          <Form.Item label="Links"><LinksFormList name="links" /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
